import {Injectable} from '@angular/core';
import { IOrganizationName } from '@core/http/configuration/interfaces/organization-config.interface';
import { IPostUserConfig } from '@core/http/configuration/interfaces/user-config.interface';
import { OrganizationConfigurationService } from '@core/http/configuration/organization-configuration.service';
import { IUserGrants } from '@modules/authentication/interfaces/authentication.interface';
import jwt_decode from 'jwt-decode';
import { lastValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  // TODO: [Estefanía Aguas] Cambiar esto a null
  private token: string | null = null;
  private currentTenant: string | null = null
  private organizationNames = new Map<string, IOrganizationName>()
  private loadingOrganizations: Promise<void> | null = null;
  private accessibleOrganizationIds!: Set<string>

  constructor(
    private readonly organizationConfigurationService: OrganizationConfigurationService
  ) {}

  onDestroy() {
    this.organizationNames.clear()
  }

  private setAccessibleOrganizations(grants: string[]) {
    if(this.token) {
      const tokenData: any = jwt_decode(this.token)
      this.accessibleOrganizationIds = new Set<string>()

      for(const entry of tokenData.user_grants) {
        if(entry.grants.some((grant: string) => grants.includes(grant))) {
          for(const org of entry.organizations) {
            this.accessibleOrganizationIds.add(org)
          }
        }
      }
    }
  }

  public async getWholeOrganizations(): Promise<Map<string, IOrganizationName>> {
    if(!this.organizationNames || !this.organizationNames.size){
      this.setAccessibleOrganizations(['LTW', 'ALL'])
      
      if(!this.loadingOrganizations) {
        this.loadingOrganizations = this.getOrganizationNames().finally(() => {
          this.loadingOrganizations = null
        })
      }

      await this.loadingOrganizations
    }

    return this.organizationNames
  }

  /**
   * Retorna el número de organizaciones accesibles por el usuario
   */
  public async getOrganizationCount(): Promise<number> {
    await this.loadingOrganizations;
    return this.organizationNames?.size ?? 0
  }

  public getAccessibleOrganizations() {
    if(!this.accessibleOrganizationIds) {
      this.setAccessibleOrganizations(['LTW', 'ALL'])
    }
    return this.accessibleOrganizationIds
  }

  public getDescendants(organizations: Map<string, IOrganizationName>, tenant: string): Map<string, IOrganizationName> {
    const organizationList = new Map(organizations)

    this.organizationConfigurationService.getDescendantsFromOrganization(tenant).subscribe({
      next: (data) => {
        if (data !== null) {
          const finalOrganizations = new Set(data)

          for(const [orgId] of organizationList) {
            if(!finalOrganizations.has(orgId)) {
                organizationList.delete(orgId)
            }
          }
        }
      },
      error: (error) => {
          return of(null)
      }
    })
    return organizationList
  }

  public setTenant(tenant: string): void {
    this.currentTenant = tenant
  }

  setToken(token: string): void {
    this.token = token;
  }

  getToken(): string | null {
    if(sessionStorage.getItem('tracr')){
      this.token = sessionStorage.getItem('tracr');
    }

    return this.token;
  }

  clearToken(): void {
    this.token = null;
  }

  getHeaders(): { [key: string]: string } {
    
    let headers: { [key: string]: string } = {}
    if (this.token)
      headers['Authorization'] = `Bearer ${this.getToken()}`;

    if (this.currentTenant)
      headers['x-requestor'] = this.currentTenant

    return headers;
  }

  public userGrantsInOrganization():string[]{
    this.token = this.getToken()
    if(!this.token) return [];
    try {
      const tokenData:any = jwt_decode(this.token);
      let  organizations = tokenData.user_grants;

      const current = this.currentTenant
      if(current)
        organizations = tokenData.user_grants.filter((organization: IUserGrants) => organization.organizations?.includes(current))[0]
      
      if (!organizations) {
        return [];
      }

      if(!organizations.grants)
        return []

      return organizations.grants
    } catch (error) {
      return [];
    }
  }

  public tokenHasGrant(tenant: string | null, grants: Array<string>):boolean{
    if (!this.token) return false;
    try {
      const tokenData:any = jwt_decode(this.token);

      if(tenant) {
        return (tokenData.user_grants as {organizations: string[]; grants: string[]}[]).some(entry =>
          entry.organizations.includes(tenant) &&
          grants.every(grant => entry.grants.includes(grant))
        );
      }

      return false
    } catch (error) {
      return false;
    }
  }

  public tokenHasAnyGrant(tenant: string | null, grants: Array<string>): boolean {
    if (!this.token) return false;
    try {
      const tokenData:any = jwt_decode(this.token);
      if(tenant) {
        return (tokenData.user_grants as {organizations: string[]; grants: string[]}[]).some(entry =>
          entry.organizations.includes(tenant) &&
          grants.some(grant => entry.grants.includes(grant))
        );
      }

      return false
    } catch (error) {
      return false;
    }
  }

  public async setInitialTenant(): Promise<void> {
    this.setAccessibleOrganizations(['LTW', 'ALL']);

    if (!this.token) return;

    const tokenData: any = jwt_decode(this.token);
    const user = tokenData.sub;

    try {
      this.currentTenant = this.accessibleOrganizationIds.values().next().value ?? null;
      const data = await lastValueFrom(
        this.organizationConfigurationService.getSpecificUserConfig(user, 'LAST_ORGANIZATION')
      );
      if (data !== null && this.accessibleOrganizationIds.has(data[0].value)) {
        this.currentTenant = data[0].value;
      } else {
        this.createLastOrganization(user)
      }
    } catch {
      this.createLastOrganization(user)
    } finally {
      if (this.currentTenant) {
        sessionStorage.setItem('currentTenant', this.currentTenant);
        this.getOrganizationNames()
      }
    }
  }

  private async getOrganizationNames(): Promise<void> {
    try {
      for(const orgId of this.accessibleOrganizationIds) {
        if(!this.organizationNames.has(orgId)) {
          const data = await lastValueFrom(
            this.organizationConfigurationService.getOrganization(orgId)
          )

          if(data) {
            this.organizationNames.set(data.id, {
              id: data.id,
              alias: data.alias,
              name: data.name
            })
          }
        }
      }
    } catch (error) {
      console.log("Error obteniendo nombres de organización", error)
    }
  }

  private createLastOrganization(user: string) {
    const body: IPostUserConfig = {
      key: 'LAST_ORGANIZATION',
      value: this.currentTenant ?? '',
      type: 'INTERFACE'
    }
    this.organizationConfigurationService.createUserConfig(user, body).subscribe()
  }

  public clearTenant() {
    this.currentTenant = null
  }

  public getTenant() {
    if(sessionStorage.getItem('currentTenant')){
      this.currentTenant = sessionStorage.getItem('currentTenant');
    }

    return this.currentTenant;
  }

  public reset() {
    this.token = null;
    this.currentTenant = null
    this.organizationNames.clear()
    this.loadingOrganizations = null;
    this.accessibleOrganizationIds.clear()
  }
}
